import { useState } from 'react'
import * as RadixDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import { Text } from './Typography.js'
import { Button } from './Button.js'
import { useCloseGuard } from './useCloseGuard.js'
import { PortalTargetContext } from './PortalTarget.js'
import { focusFirstField } from './openFocus.js'

export interface DialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  /** Under the title. What this is for, in one line. */
  description?: React.ReactNode
  /** The mark on the left of the title. See `Sheet` for what it is for. */
  icon?: React.ReactNode
  children?: React.ReactNode
  footer?: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
  /** Somebody has typed something that is not saved. See `useCloseGuard`. */
  dirty?: boolean
  /** What Enter does. See `Sheet`, which explains it. */
  onSubmit?: () => void
}

/**
 * A panel over the screen, for something that needs the whole of somebody's
 * attention and returns them to where they were.
 *
 * The title is required. A dialog with no heading is a dialog a screen reader
 * announces as "dialog", which tells the one person who most needs to know the
 * least.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  children,
  footer,
  size = 'md',
  dirty,
  onSubmit,
}: DialogProps) {
  const { guardedOpenChange, guard } = useCloseGuard(dirty, onOpenChange)
  /* See `Sheet`, and `PortalTarget` for why. */
  const [content, setContent] = useState<HTMLDivElement | null>(null)
  return (
    <>
      <RadixDialog.Root open={open} onOpenChange={guardedOpenChange}>
        <RadixDialog.Portal>
          <RadixDialog.Overlay className="fixed inset-0 z-50 bg-navy/40 data-[state=open]:animate-in data-[state=open]:fade-in" />
          <RadixDialog.Content
            ref={setContent}
            onOpenAutoFocus={focusFirstField}
            className={cn(
              'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
              'rounded-lg border border-hairline bg-surface p-6 shadow-lg outline-none',
              size === 'sm' && 'max-w-md',
              size === 'md' && 'max-w-lg',
              size === 'lg' && 'max-w-2xl',
            )}
          >
            <PortalTargetContext.Provider value={content}>
              <div
                className={cn(
                  'flex justify-between gap-4',
                  description ? 'items-start' : 'items-center',
                )}
              >
                {icon && (
                  <span
                    aria-hidden
                    className="bg-sunk text-ink-2 flex size-10 shrink-0 items-center justify-center rounded-md [&_svg]:size-5"
                  >
                    {icon}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <RadixDialog.Title asChild>
                    <Text as="h2" variant="title">
                      {title}
                    </Text>
                  </RadixDialog.Title>
                  {description && (
                    <RadixDialog.Description asChild>
                      <Text
                        /* A `div` when it is more than a sentence, or a `<p>`
                           holds `<p>`s — which React warned about on every
                           suspend and pause question. */
                        as={typeof description === 'string' ? 'p' : 'div'}
                        variant="secondary"
                        className="mt-1"
                      >
                        {description}
                      </Text>
                    </RadixDialog.Description>
                  )}
                </div>
                <RadixDialog.Close asChild>
                  {/* Round and 32px, the same treatment as the search box's
                  clear — a square the size of a real button beside a 20px
                  title is the thing setting the header's height. */}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Close"
                    className="size-8 rounded-full"
                  >
                    <X />
                  </Button>
                </RadixDialog.Close>
              </div>
              <Body onSubmit={onSubmit}>
                {children && <div className="mt-5">{children}</div>}
                {footer && (
                  <div className="mt-6 flex flex-wrap items-center justify-end gap-2">{footer}</div>
                )}
              </Body>
            </PortalTargetContext.Provider>
          </RadixDialog.Content>
        </RadixDialog.Portal>
      </RadixDialog.Root>
      {guard}
    </>
  )
}

/** See `Sheet`'s `Body`. */
function Body({ onSubmit, children }: { onSubmit?: () => void; children: React.ReactNode }) {
  if (!onSubmit) return <>{children}</>
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      {children}
    </form>
  )
}
